<template>
	<div class="image-slideshow" @keydown.left="prev" @keydown.right="next" tabindex="0" :style="sizeStyle">
		<icon-button v-if="showCarousel" variant="left" @click="prev" />
		<div ref="inner" :class="['inner', transitionClass, {'carousel': showCarousel}]">
			<div
				v-for="(image, index) of carouselArray"
				:key="'image-slide-show-' + image.index"
				class="card"
			>
				<ximg
					v-if="showImage(index)"
					:lazy="false"
					:src="image.item.zoom"
					no-upscale
					:previewUrl="showPreviews ? image.item.thumb : null"
					:ariaLabel="'zoomed in image number ' + image.index + ' of ' + imageName"
					role="img"
				/>
			</div>
		</div>
		<icon-button v-if="showCarousel" variant="right" @click="next" />
		<v-touch style="position: absolute;" :style="contentStyle" @swipeleft="next" @swiperight="prev">
			<span class="invisible-scroll _left" @click="prev" />
			<span class="invisible-scroll _right" @click="next" />
		</v-touch>
		<div v-if="images.length > 1" class="gallery-dot-wrap">
			<span
				class="gallery-dot"
				v-for="index in images.length"
				:key="'slideshow-gallery-dot-' + index"
				:class="{ active: index - 1 === currentImageIndex }"
				@click="handleDotClick(index)"
			/>
		</div>
	</div>
</template>

<script>
import mq from 'mediaQuery';
import { t } from 'datastore/i18n';

export default {
	props: {
		images: {
			type: Array,
			default: null,
		},
		initialImageIndex: {
			type: Number,
			default: 0,
		},
		noPreviews: {
			default: false,
		},
		imageName: {
			type: String,
			default: '',
		},
	},
	data () {
		return {
			mq,
			currentImageIndex: null,
			carouselArray: [],
			transitioning: false,
			transitionClass: null,
		};
	},
	mounted () {
		this.currentImageIndex = this.initialImageIndex;
		this.$el.focus();
		this.createCarouselArray(this.initialImageIndex);
	},
	methods: {
		t,
		createCarouselArray (startValue) {
			this.carouselArray = [];
			/* When there's only 2 images in product,
			this duplicates the content of the array so that there's always 3 images loaded. */
			if (this.images.length === 2) {
				this.carouselArray.push(
					{
						index: 0,
						item: this.images[1],
					},
					{
						index: 1,
						item: this.images[0],
					},
					{
						index: 2,
						item: this.images[1],
					},
					{
						index: 3,
						item: this.images[0],
					}
				);
				if (this.initialImageIndex === 1) {
					return this.carouselArray.reverse();
				}
			}
			/* If there's more than 2 images or 1 image,
			set the first image in the carousel to initialImageIndex,
			and set images in carousel to the right order. */
			else {
				// Save initialImageIndex so that index is always unique.
				let newIndex = startValue;
				for (let i = 0; i < this.images.length; i++) {
					// Makes sure indexes above images.length won't get added.
					if (newIndex < this.images.length) {
						this.carouselArray.push({
							index: newIndex,
							item: this.images[newIndex],
						});
						newIndex++;
						// If initialImageIndex is above 0 start adding index from beginning of array.
					} else {
						const newStartIndex = newIndex - this.images.length;
						this.carouselArray.push({
							index: newStartIndex,
							item: this.images[newStartIndex],
						});
						newIndex++;
					}
				}
				this.shiftArray('prev');
			}
		},
		showImage(index) {
			if (this.images.length === 1) {
				return true;
			} else if (index < 3) {
				return true;
			} else {
				return false;
			}
		},
		// Adds an image to the left of currentImage, so that the carousel can move to the left.
		shiftArray (direction) {
			let card = null;
			switch (direction) {
				case 'next' :
					card = this.carouselArray.shift();
					this.carouselArray.push(card);
					break;
				case 'prev' :
					card = this.carouselArray.pop();
					this.carouselArray.unshift(card);
					break;
			}
		},
		next () {
			if (this.images.length > 1) {
				if (this.transitioning) return;
				this.transitioning = true;
				this.transitionClass = 'move-left';
				this.afterTransition(() => {
					this.shiftArray('next');
					this.transitionClass = null;
					this.currentImageIndex++;
					this.transitioning = false;
				});
			}
		},
		prev () {
			if (this.images.length > 1) {
				if (this.transitioning) return;
				this.transitioning = true;
				this.transitionClass = 'move-right';
				this.afterTransition(() => {
					this.shiftArray('prev');
					this.transitionClass = null;
					this.currentImageIndex--;
					this.transitioning = false;
				});
			}
		},
		afterTransition (callback) {
			const listener = () => {
				callback();
				this.$refs.inner.removeEventListener('transitionend', listener);
			};
			this.$refs.inner.addEventListener('transitionend', listener);
		},
		handleDotClick(index){
			const newIndex = index - 1;
			if (this.images.length <= 3){
				if ((this.currentImageIndex < newIndex && this.currentImageIndex - newIndex === -1)
					|| (this.currentImageIndex > newIndex && this.currentImageIndex - newIndex === 2)) {
					this.next();
				} else {
					this.prev();
				}
			} else {
				this.currentImageIndex = newIndex;
				this.createCarouselArray(this.currentImageIndex);
			}
		},
	},
	computed: {
		showCarousel () {
			return this.images.length > 1;
		},
		showPreviews () {
			return this.noPreviews === false;
		},
		sizeStyle () {
			return this.mq.pageWidth < this.mq.pageHeight ? {
				width: '90vw',
				height: '80vh',
			} : {
				width: '85vw',
				height: '85vh',
			};
		},
		contentStyle () {
			const xMargin = this.mq.current < this.mq.md ? 32 : 64;
			const yMargin = 80;
			return {
				top: yMargin + 'px',
				left: xMargin + 'px',
				right: xMargin + 'px',
				bottom: yMargin + 'px',
			};
		},
	},
	watch: {
		currentImageIndex (to) {
			if (to < 0) {
				this.currentImageIndex = this.images.length - 1;
			}
			this.currentImageIndex %= this.images.length;
		},
	},
};
</script>

